(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function initialiseMeruMotion(root = document) {
    const home = root.querySelector?.('.meru-home--editorial');
    if (!home || home.dataset.motionReady === 'true') return;
    home.dataset.motionReady = 'true';

    home.querySelectorAll('.meru-collection-nav__carousel').forEach((carousel) => {
      const track = carousel.querySelector('.meru-collection-nav__track');
      const previous = carousel.querySelector('.meru-collection-nav__arrow--prev');
      const next = carousel.querySelector('.meru-collection-nav__arrow--next');
      if (!track || !previous || !next) return;

      const updateControls = () => {
        const end = Math.max(0, track.scrollWidth - track.clientWidth);
        previous.hidden = track.scrollLeft <= 4;
        next.hidden = track.scrollLeft >= end - 4;
      };
      const move = (direction) => track.scrollBy({
        left: direction * track.clientWidth,
        behavior: reduceMotion.matches ? 'auto' : 'smooth',
      });

      previous.addEventListener('click', () => move(-1));
      next.addEventListener('click', () => move(1));
      track.addEventListener('scroll', updateControls, { passive: true });
      window.addEventListener('resize', updateControls, { passive: true });
      updateControls();
    });

    home.querySelectorAll('[data-meru-hero]').forEach((hero) => {
      const slides = [...hero.querySelectorAll('[data-meru-hero-slide]')];
      const dots = [...hero.querySelectorAll('[data-meru-hero-dot]')];
      const previous = hero.querySelector('[data-meru-hero-prev]');
      const next = hero.querySelector('[data-meru-hero-next]');
      if (slides.length < 2 || !previous || !next) return;

      let current = 0;
      let timer;
      let touchStartX = 0;
      let paused = false;
      let pendingShow = 0;

      const loadSlide = (slide) => {
        slide.querySelectorAll('[data-meru-srcset]').forEach((element) => {
          element.srcset = element.dataset.meruSrcset;
          delete element.dataset.meruSrcset;
        });
        const images = [...slide.querySelectorAll('img[data-meru-src]')].filter((image) => {
          return !image.dataset.meruMedia || window.matchMedia(image.dataset.meruMedia).matches;
        });
        images.forEach((image) => {
          image.src = image.dataset.meruSrc;
          delete image.dataset.meruSrc;
        });
        return Promise.all(images.map((image) => {
          if (image.complete) return Promise.resolve();
          return new Promise((resolve) => {
            image.addEventListener('load', resolve, { once: true });
            image.addEventListener('error', resolve, { once: true });
          });
        }));
      };

      const show = async (index, restart = true) => {
        const nextIndex = (index + slides.length) % slides.length;
        const request = ++pendingShow;
        await loadSlide(slides[nextIndex]);
        if (request !== pendingShow) return;
        current = nextIndex;
        slides.forEach((slide, slideIndex) => {
          const active = slideIndex === current;
          slide.classList.toggle('is-active', active);
          slide.setAttribute('aria-hidden', String(!active));
          slide.querySelectorAll('a, button').forEach((control) => {
            control.tabIndex = active ? 0 : -1;
          });
        });
        dots.forEach((dot, dotIndex) => {
          const active = dotIndex === current;
          dot.classList.toggle('is-active', active);
          dot.setAttribute('aria-current', String(active));
        });
        if (restart) start();
      };

      const stop = () => window.clearInterval(timer);
      const start = () => {
        stop();
        if (reduceMotion.matches || paused || document.hidden) return;
        timer = window.setInterval(() => show(current + 1, false), 6500);
      };

      previous.addEventListener('click', () => show(current - 1));
      next.addEventListener('click', () => show(current + 1));
      dots.forEach((dot, index) => dot.addEventListener('click', () => show(index)));
      hero.addEventListener('mouseenter', () => { paused = true; stop(); });
      hero.addEventListener('mouseleave', () => { paused = false; start(); });
      hero.addEventListener('focusin', () => { paused = true; stop(); });
      hero.addEventListener('focusout', (event) => {
        if (hero.contains(event.relatedTarget)) return;
        paused = false;
        start();
      });
      hero.addEventListener('touchstart', (event) => {
        touchStartX = event.changedTouches[0]?.clientX || 0;
        stop();
      }, { passive: true });
      hero.addEventListener('touchend', (event) => {
        const distance = (event.changedTouches[0]?.clientX || 0) - touchStartX;
        if (Math.abs(distance) > 45) show(current + (distance < 0 ? 1 : -1));
        else start();
      }, { passive: true });
      document.addEventListener('visibilitychange', start);
      window.addEventListener('resize', () => loadSlide(slides[current]), { passive: true });
      show(0);
    });

    if (reduceMotion.matches) {
      home.classList.add('meru-motion-reduced');
      return;
    }

    home.classList.add('meru-motion-ready');
    const revealGroups = [
      ['.meru-collection-nav .meru-section-heading', 0],
      ['.meru-collection-nav__track > a', 70],
      ['.meru-product-showcase .meru-section-heading', 0],
      ['.meru-preview-products > article', 65],
      ['.meru-editorial-banner__copy', 0],
      ['.meru-brand-signature__content > *', 70],
      ['.meru-story__media, .meru-story__copy', 100],
      ['.meru-newsletter > *', 90],
    ];
    const reveals = [];

    revealGroups.forEach(([selector, stagger]) => {
      home.querySelectorAll(selector).forEach((element, index) => {
        element.classList.add('meru-reveal');
        element.style.setProperty('--meru-reveal-delay', `${Math.min(index * stagger, 260)}ms`);
        reveals.push(element);
      });
    });

    if (!('IntersectionObserver' in window)) {
      reveals.forEach((element) => element.classList.add('is-visible'));
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });

    reveals.forEach((element) => observer.observe(element));
  }

  const boot = () => initialiseMeruMotion(document);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();

  document.addEventListener('shopify:section:load', (event) => initialiseMeruMotion(event.target));
  document.addEventListener('click', (event) => {
    if (!event.target.closest('add-to-cart-component, .quick-add__button')) return;
    const cartIcon = document.querySelector('.header-actions__cart-icon');
    if (!cartIcon || reduceMotion.matches) return;
    cartIcon.classList.remove('meru-cart-pulse');
    requestAnimationFrame(() => cartIcon.classList.add('meru-cart-pulse'));
    window.setTimeout(() => cartIcon.classList.remove('meru-cart-pulse'), 700);
  });
})();
